import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import ImageModalEquip from "../Modal/ImageModalProd";
import "../CRMUpdatedVersion/LeadsUpdatedVersion.css";
import { getDiscountValidationError } from "../Equipments/productPricingValidation";

const calculateSalePrice = (mrp, discountType, discountAmount, discountPercentage) => {
    const base = Number(mrp) || 0;
    let salePrice = 0;

    if (discountType === "price") {
        salePrice = base - (Number(discountAmount) || 0);
    } else if (discountType === "percentage") {
        const pct = Number(discountPercentage) || 0;
        salePrice = base - (base * pct) / 100;
    }

    if (salePrice < 0) salePrice = 0;
    return base ? salePrice.toFixed(2) : "";
};

const generateSku = (prefix = "SM") =>
    `${prefix}${Date.now().toString(36).toUpperCase().slice(-7)}`;

const generateVariationSku = () => {
    const time = Date.now().toString(36).toUpperCase().slice(-5);
    const rand = Math.random().toString(36).toUpperCase().slice(2, 4);
    return `VP${time}${rand}`;
};

const cartesianProduct = (arrays) =>
    arrays.reduce((acc, curr) => acc.flatMap((a) => curr.map((c) => [...a, c])), [[]]);

const PRODUCT_ENDPOINTS = {
    "Simple Product": "/simple-product/admin1997",
    "Variable Product": "/variable-product/admin1997",
    "Grouped Product": "/grouped-product/admin1997",
};

const PRODUCT_ICONS = {
    "Simple Product": "fa-solid fa-box",
    "Variable Product": "fa-solid fa-layer-group",
    "Grouped Product": "fa-solid fa-boxes-stacked",
};

const STOCK_OPTIONS = ["In stock", "Out of stock", "Unlimited Stock"];

const InvoiceAddProductFormModal = ({ productType, isOpen, onClose, onSuccess }) => {
    const user = getDecryptedUserFromCookie("gta_mainstateuser");
    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";
    const [loading, setLoading] = useState(false);

    const [title, setTitle] = useState("");
    const [shortDescription, setShortDescription] = useState("");
    const [sku, setSku] = useState("");
    const [originalPrice, setOriginalPrice] = useState("");
    const [discountPrice, setDiscountPrice] = useState("");
    const [discountType, setDiscountType] = useState("price");
    const [discountAmount, setDiscountAmount] = useState("");
    const [discountPercentage, setDiscountPercentage] = useState("");
    const [hsn, setHsn] = useState("");
    const [totalStock, setTotalStock] = useState("");
    const [stockStatus, setStockStatus] = useState("In stock");
    const [productStatus, setProductStatus] = useState("Active");
    const [categoryId, setCategoryId] = useState("");
    const [categories, setCategories] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [images, setImages] = useState([]);

    const [attributeList, setAttributeList] = useState([]);
    const [selectedAttributes, setSelectedAttributes] = useState([]);
    const [savedAttributeIds, setSavedAttributeIds] = useState([]);
    const [attributeSearch, setAttributeSearch] = useState("");
    const [filteredAttributes, setFilteredAttributes] = useState([]);
    const [attributeAccordions, setAttributeAccordions] = useState({});
    const [variations, setVariations] = useState([]);
    const [variationDetails, setVariationDetails] = useState({});
    const [activeVariationIndex, setActiveVariationIndex] = useState(null);

    const [productSearch, setProductSearch] = useState("");
    const [allProducts, setAllProducts] = useState([]);
    const [groupedProducts, setGroupedProducts] = useState([]);

    const resetForm = () => {
        setTitle("");
        setShortDescription("");
        setSku("");
        setOriginalPrice("");
        setDiscountPrice("");
        setDiscountType("price");
        setDiscountAmount("");
        setDiscountPercentage("");
        setHsn("");
        setTotalStock("");
        setStockStatus("In stock");
        setProductStatus("Active");
        setCategoryId("");
        setSelectedImage(null);
        setAttributeList([]);
        setSelectedAttributes([]);
        setSavedAttributeIds([]);
        setAttributeSearch("");
        setFilteredAttributes([]);
        setAttributeAccordions({});
        setVariations([]);
        setVariationDetails({});
        setActiveVariationIndex(null);
        setProductSearch("");
        setGroupedProducts([]);
    };

    useEffect(() => {
        if (!isOpen || !productType) return;
        resetForm();
        if (productType === "Simple Product") {
            setSku(generateSku("SM"));
        } else if (productType === "Variable Product") {
            setStockStatus("Unlimited Stock");
        } else if (productType === "Grouped Product") {
            setSku(generateSku("GP"));
        }
    }, [isOpen, productType]);

    useEffect(() => {
        if (!isOpen || !user?.main_id) return;

        fetch(`${url}/category/getdata/category/${user.main_id}`)
            .then((res) => res.json())
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                setCategories(
                    list.map((cat) => ({
                        id: String(cat.id),
                        label: cat.category || "Unnamed category",
                    }))
                );
            })
            .catch(console.error);

        axios
            .get(`${url}/api/images/${user.main_id}`)
            .then((res) => setImages(Array.isArray(res.data) ? res.data.reverse() : []))
            .catch(console.error);
    }, [isOpen, user?.main_id, url]);

    useEffect(() => {
        if (!isOpen || productType !== "Grouped Product" || !user?.main_id) return;
        axios
            .get(`${url}/grouped-product/search-all-products/${user.main_id}`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { includeInactive: true },
            })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                setAllProducts(list.filter((p) => p.product_type !== "Grouped Product"));
            })
            .catch(console.error);
    }, [isOpen, productType, user?.main_id, url, token]);

    useEffect(() => {
        if (!isOpen || productType !== "Variable Product" || !user?.main_id) return;
        fetch(`${url}/attribute/getdata/${user.main_id}`)
            .then((res) => res.json())
            .then((data) => setAttributeList(Array.isArray(data) ? data : []))
            .catch(console.error);
    }, [isOpen, productType, user?.main_id, url]);

    const filteredProducts = productSearch.trim()
        ? allProducts.filter((p) =>
              p.title?.toLowerCase().includes(productSearch.toLowerCase())
          )
        : [];

    const addGroupedProduct = (product) => {
        if (groupedProducts.some((p) => String(p.product_id) === String(product.product_id))) {
            toast.info("Product already added to group.");
            return;
        }
        setGroupedProducts((prev) => [...prev, product]);
        setProductSearch("");
    };

    const removeGroupedProduct = (productId) => {
        setGroupedProducts((prev) =>
            prev.filter((p) => String(p.product_id) !== String(productId))
        );
    };

    const handleAttributeSearch = (e) => {
        const query = e.target.value;
        setAttributeSearch(query);
        if (!query.trim()) {
            setFilteredAttributes([]);
            return;
        }
        setFilteredAttributes(
            attributeList.filter((attr) =>
                attr.name?.toLowerCase().includes(query.toLowerCase())
            )
        );
    };

    const handleAttributeSelect = (attribute) => {
        if (selectedAttributes.some((a) => a.id === attribute.id)) return;
        setSelectedAttributes((prev) => [...prev, { ...attribute, selectedTerms: [] }]);
        setAttributeSearch("");
        setFilteredAttributes([]);
        setAttributeAccordions((prev) => ({ ...prev, [attribute.name]: true }));
    };

    const toggleAttributeAccordion = (name) => {
        setAttributeAccordions((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const handleDeleteAttribute = (attributeId) => {
        setSelectedAttributes((prev) => prev.filter((a) => a.id !== attributeId));
        setSavedAttributeIds((prev) => prev.filter((id) => id !== attributeId));
        setVariations([]);
        setVariationDetails({});
        setActiveVariationIndex(null);
    };

    const handleTermClick = (attribute, term) => {
        setSelectedAttributes((prev) =>
            prev.map((attr) => {
                if (attr.id !== attribute.id) return attr;
                const isSelected = attr.selectedTerms.some((t) => t.id === term.id);
                return {
                    ...attr,
                    selectedTerms: isSelected
                        ? attr.selectedTerms.filter((t) => t.id !== term.id)
                        : [...attr.selectedTerms, term],
                };
            })
        );
        setSavedAttributeIds((prev) => prev.filter((id) => id !== attribute.id));
    };

    const handleSelectAllTerms = (e, attribute) => {
        e.preventDefault();
        setSelectedAttributes((prev) =>
            prev.map((attr) =>
                attr.id === attribute.id
                    ? { ...attr, selectedTerms: [...attribute.associatedTerms] }
                    : attr
            )
        );
        setSavedAttributeIds((prev) => prev.filter((id) => id !== attribute.id));
    };

    const handleDeselectAllTerms = (e, attribute) => {
        e.preventDefault();
        setSelectedAttributes((prev) =>
            prev.map((attr) =>
                attr.id === attribute.id ? { ...attr, selectedTerms: [] } : attr
            )
        );
        setSavedAttributeIds((prev) => prev.filter((id) => id !== attribute.id));
    };

    const handleSaveAttribute = (e, attribute) => {
        e.preventDefault();
        if (!attribute.selectedTerms.length) {
            toast.error(`Select at least one value for ${attribute.name}.`);
            return;
        }
        setSavedAttributeIds((prev) =>
            prev.includes(attribute.id) ? prev : [...prev, attribute.id]
        );
    };

    const handleGenerateVariations = (e) => {
        e.preventDefault();
        const termArrays = selectedAttributes.map((attr) =>
            attr.selectedTerms.map((term) => ({ attrId: attr.id, termId: term.id }))
        );
        const combinations = cartesianProduct(termArrays);
        const newVariations = combinations.map((combo, idx) => ({
            id: `v${idx}${Date.now()}`,
            attributes: Object.fromEntries(combo.map((item) => [item.attrId, item.termId])),
            price: "",
            salePrice: "",
            title: "",
            sku: generateVariationSku(),
            stock: "",
            discount_type: "price",
            discount_price: "",
            discount_percentage: "",
        }));
        setVariations(newVariations);
        setVariationDetails({});
        setActiveVariationIndex(null);
    };

    const handleRemoveVariation = (id) => {
        setVariations((prev) => prev.filter((v) => v.id !== id));
        setVariationDetails((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const handleVariationDetailChange = (id, field, value) => {
        const current = { ...(variationDetails[id] || {}), [field]: value };
        const regularPrice = parseFloat(field === "price" ? value : current.price) || 0;
        const discountType = current.discount_type || "price";
        const discountPriceVal = parseFloat(current.discount_price) || 0;
        const discountPct = parseFloat(current.discount_percentage) || 0;

        if (regularPrice > 0) {
            let salePrice = regularPrice;
            if (discountType === "price") {
                salePrice = regularPrice - discountPriceVal;
            } else if (discountType === "percentage") {
                salePrice = regularPrice - (regularPrice * discountPct) / 100;
            }
            if (salePrice < 0) salePrice = 0;
            current.salePrice = salePrice.toFixed(2);
        }

        setVariationDetails((prev) => ({ ...prev, [id]: current }));
        setVariations((prev) =>
            prev.map((v) =>
                v.id === id
                    ? {
                          ...v,
                          title: current.title ?? v.title,
                          price: current.price ?? v.price,
                          salePrice: current.salePrice ?? v.salePrice,
                          sku: current.sku ?? v.sku,
                          stock: current.stock ?? v.stock,
                          discount_type: current.discount_type ?? v.discount_type,
                          discount_price: current.discount_price ?? v.discount_price,
                          discount_percentage:
                              current.discount_percentage ?? v.discount_percentage,
                      }
                    : v
            )
        );
    };

    const getVariationLabel = (variation, index) => {
        const label = Object.entries(variation.attributes || {})
            .map(([attrId, termId]) => {
                const attr = selectedAttributes.find((a) => a.id === Number(attrId));
                const term = attr?.associatedTerms?.find((t) => t.id === Number(termId));
                return `${attr?.name || "Unknown"}: ${term?.name || "N/A"}`;
            })
            .join(" & ");
        return label || `Variation #${index + 1}`;
    };

    const mergeVariationForPayload = (variation) => {
        const details = variationDetails[variation.id] || {};
        return {
            ...variation,
            title: details.title ?? variation.title ?? "",
            price: details.price ?? variation.price ?? "",
            salePrice: details.salePrice ?? variation.salePrice ?? "",
            sku: (details.sku ?? variation.sku ?? "").toString().toUpperCase(),
            stock: details.stock ?? variation.stock ?? 0,
            discount_type: details.discount_type ?? variation.discount_type ?? "price",
            discount_price: details.discount_price ?? variation.discount_price ?? "",
            discount_percentage:
                details.discount_percentage ?? variation.discount_percentage ?? "",
        };
    };

    const checkDuplicateName = async () => {
        const existingRes = await axios.get(
            `${url}/grouped-product/search-all-products/${encodeURIComponent(user.main_id)}`,
            { headers: { Authorization: `Bearer ${token}` }, params: { includeInactive: true } }
        );
        const existingProducts = Array.isArray(existingRes.data) ? existingRes.data : [];
        return existingProducts.some(
            (p) => p.title?.trim().toLowerCase() === title.trim().toLowerCase()
        );
    };

    const buildPayload = () => {
        const selectedCategory = categories.find((c) => c.id === categoryId);
        const base = {
            title: title.trim(),
            short_description: shortDescription.trim(),
            description: "",
            image: selectedImage?.url || "",
            category: selectedCategory?.label || "",
            category_id: categoryId || "",
            categorytype: "",
            categorytype_id: [],
            status: productStatus,
            stock: stockStatus,
            typeofsimple: "virtual",
            warranty: "Yes",
            warranty_from: "",
            installation: "",
            installation_fee: "",
            user_name: user?.full_name || "",
            user_id: user?.main_id || "",
            brand_name: "",
            brand_id: [],
            hsn: hsn.trim(),
            discount_type: discountType,
            discount_price: discountAmount,
            discount_percentage: discountPercentage,
            gallery: [],
            specifications: [],
            key_features: [],
            downloadable_files: [],
            download_limit: null,
            download_expiry: null,
            delivery_days: "",
        };

        if (productType === "Simple Product") {
            return {
                ...base,
                sku: sku.trim(),
                originalPrice,
                discountPrice,
                totalStock: stockStatus === "Unlimited Stock" ? "" : totalStock,
                product_type: "Simple Product",
            };
        }

        if (productType === "Variable Product") {
            return {
                ...base,
                product_type: "Variable Product",
                gst_type: "included",
                gst_percentage: "",
                attribute: selectedAttributes.map((attr) => ({
                    id: attr.id,
                    selectedTermIds: attr.selectedTerms.map((term) => term.id),
                })),
                variation: variations.map(mergeVariationForPayload),
            };
        }

        return {
            ...base,
            sku: sku.trim(),
            originalPrice,
            discountPrice,
            product_type: "Grouped Product",
            grouped_product: groupedProducts,
            grouped_products: groupedProducts,
            gst_type: "included",
            gst_percentage: "",
        };
    };

    const validate = () => {
        if (!title.trim()) {
            toast.error("Product name is required.");
            return false;
        }

        if (productType === "Simple Product") {
            if (!sku.trim()) {
                toast.error("SKU is required.");
                return false;
            }
            if (!originalPrice && !discountPrice) {
                toast.error("MRP or sale price is required.");
                return false;
            }
            const discountError = getDiscountValidationError(
                {
                    discount_type: discountType,
                    discount_price: discountAmount,
                    discount_percentage: discountPercentage,
                },
                originalPrice,
                { baseLabel: "MRP" }
            );
            if (discountError) {
                toast.error(discountError);
                return false;
            }
            if (
                originalPrice &&
                discountPrice &&
                Number(discountPrice) > Number(originalPrice)
            ) {
                toast.error("Sale price cannot be greater than MRP.");
                return false;
            }
            if (stockStatus !== "Unlimited Stock" && !String(totalStock).trim()) {
                toast.error("Total stock is required.");
                return false;
            }
        }

        if (productType === "Variable Product") {
            if (selectedAttributes.length === 0) {
                toast.error("Add at least one attribute.");
                return false;
            }
            if (savedAttributeIds.length !== selectedAttributes.length) {
                toast.error("Save all attributes before generating variations.");
                return false;
            }
            if (variations.length === 0) {
                toast.error("Generate at least one variation.");
                return false;
            }
            for (let i = 0; i < variations.length; i += 1) {
                const merged = mergeVariationForPayload(variations[i]);
                const label = getVariationLabel(variations[i], i);
                if (!merged.sku?.toString().trim()) {
                    toast.error(`SKU is required for ${label}.`);
                    setActiveVariationIndex(i);
                    return false;
                }
                if (!merged.price && !merged.salePrice) {
                    toast.error(`Regular price or sale price is required for ${label}.`);
                    setActiveVariationIndex(i);
                    return false;
                }
                const discountErr = getDiscountValidationError(
                    {
                        discount_type: merged.discount_type,
                        discount_price: merged.discount_price,
                        discount_percentage: merged.discount_percentage,
                    },
                    merged.price,
                    { baseLabel: "regular price" }
                );
                if (discountErr) {
                    toast.error(`${discountErr} (${label})`);
                    setActiveVariationIndex(i);
                    return false;
                }
                if (
                    merged.price &&
                    merged.salePrice &&
                    Number(merged.salePrice) > Number(merged.price)
                ) {
                    toast.error(`Sale price cannot be greater than regular price. (${label})`);
                    setActiveVariationIndex(i);
                    return false;
                }
            }
        }

        if (productType === "Grouped Product") {
            if (!sku.trim()) {
                toast.error("SKU is required.");
                return false;
            }
            if (!originalPrice && !discountPrice) {
                toast.error("Original price or sale price is required.");
                return false;
            }
            const groupedDiscountError = getDiscountValidationError(
                {
                    discount_type: discountType,
                    discount_price: discountAmount,
                    discount_percentage: discountPercentage,
                },
                originalPrice,
                { baseLabel: "original price" }
            );
            if (groupedDiscountError) {
                toast.error(groupedDiscountError);
                return false;
            }
            if (
                originalPrice &&
                discountPrice &&
                Number(discountPrice) > Number(originalPrice)
            ) {
                toast.error("Sale price cannot be greater than original price.");
                return false;
            }
            if (groupedProducts.length === 0) {
                toast.error("Add at least one product to the group.");
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!productType || !validate()) return;

        setLoading(true);
        try {
            const nameExists = await checkDuplicateName();
            if (nameExists) {
                toast.warning("Product name already exists. Please use a different name.", {
                    position: "top-center",
                    autoClose: 3000,
                });
                setLoading(false);
                return;
            }

            const endpoint = `${url}${PRODUCT_ENDPOINTS[productType]}`;
            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(buildPayload()),
            });
            const res = await response.json();

            if (response.ok) {
                toast.success("Product created successfully!", {
                    position: "top-center",
                    autoClose: 3000,
                });
                onSuccess?.();
                onClose();
            } else {
                toast.error(res.message || "Failed to create product.", {
                    position: "top-center",
                    autoClose: 3000,
                });
            }
        } catch (error) {
            toast.error("Failed to create product. Please try again.", {
                position: "top-center",
                autoClose: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const renderImageField = () => (
        <div className="ls-form-group">
            <label>Main Product Image</label>
            <button
                type="button"
                className="ls-apply-btn"
                style={{ width: "100%", marginBottom: selectedImage ? 8 : 0 }}
                onClick={() => setShowImageModal(true)}
            >
                Choose Product Image
            </button>
            {selectedImage?.url && (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <img
                        src={`${url}${selectedImage.url}`}
                        alt="Product"
                        style={{ width: 56, height: 56, objectFit: "contain", borderRadius: 6, border: "1px solid #ddd" }}
                    />
                    <button
                        type="button"
                        className="quotation-close"
                        onClick={() => setSelectedImage(null)}
                    >
                        Remove
                    </button>
                </div>
            )}
        </div>
    );

    const renderCategoryField = () => (
        <div className="ls-form-group">
            <label>Product Category</label>
            <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="ls-categoryservice"
            >
                <option value="">Select category</option>
                {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                        {cat.label}
                    </option>
                ))}
            </select>
        </div>
    );

    const renderStatusField = () => (
        <div className="ls-form-group">
            <label>Product Status</label>
            <div className="ls-radio-group">
                <label
                    className={`ls-radio-option ${productStatus === "Active" ? "selected" : ""}`}
                    onClick={() => setProductStatus("Active")}
                >
                    Active
                </label>
                <label
                    className={`ls-radio-option ${productStatus === "Inactive" ? "selected" : ""}`}
                    onClick={() => setProductStatus("Inactive")}
                >
                    Inactive
                </label>
            </div>
        </div>
    );

    const recalculateSalePrice = (mrp, type, amount, percentage) => {
        const hasDiscount =
            (type === "price" && amount !== "") ||
            (type === "percentage" && percentage !== "");
        if (!hasDiscount) return null;
        return calculateSalePrice(mrp, type, amount, percentage);
    };

    const renderPricingFields = (mrpLabel) => (
        <>
            <div className="ls-form-group">
                <label>{mrpLabel}</label>
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={`Enter ${mrpLabel.replace(/\*$/, "").trim().toLowerCase()}`}
                    value={originalPrice}
                    onChange={(e) => {
                        const value = e.target.value;
                        setOriginalPrice(value);
                        const nextSale = recalculateSalePrice(
                            value,
                            discountType,
                            discountAmount,
                            discountPercentage
                        );
                        if (nextSale !== null) setDiscountPrice(nextSale);
                    }}
                />
            </div>
            <div className="ls-form-group">
                <label>Discount Type</label>
                <div className="ls-radio-group">
                    <label
                        className={`ls-radio-option ${discountType === "price" ? "selected" : ""}`}
                        onClick={() => {
                            setDiscountType("price");
                            setDiscountPercentage("");
                        }}
                    >
                        Price
                    </label>
                    <label
                        className={`ls-radio-option ${discountType === "percentage" ? "selected" : ""}`}
                        onClick={() => {
                            setDiscountType("percentage");
                            setDiscountAmount("");
                        }}
                    >
                        Percentage
                    </label>
                </div>
            </div>
            {discountType === "price" ? (
                <div className="ls-form-group">
                    <label>Discount Price</label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Enter discount price"
                        value={discountAmount}
                        onChange={(e) => {
                            const value = e.target.value;
                            setDiscountAmount(value);
                            setDiscountPercentage("");
                            setDiscountPrice(
                                calculateSalePrice(originalPrice, "price", value, "")
                            );
                        }}
                    />
                </div>
            ) : (
                <div className="ls-form-group">
                    <label>Discount Percentage (%)</label>
                    <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="Enter discount percentage"
                        value={discountPercentage}
                        onChange={(e) => {
                            const value = e.target.value;
                            setDiscountPercentage(value);
                            setDiscountAmount("");
                            setDiscountPrice(
                                calculateSalePrice(originalPrice, "percentage", "", value)
                            );
                        }}
                    />
                </div>
            )}
            <div className="ls-form-group">
                <label>Sale Price*</label>
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter sale price"
                    value={discountPrice}
                    onChange={(e) => setDiscountPrice(e.target.value)}
                />
            </div>
        </>
    );

    const renderVariableProductFields = () => (
        <>
            <div className="ls-form-group">
                <label>Select Attributes*</label>
                <input
                    type="text"
                    placeholder="Search attributes (e.g. Color, Size)"
                    value={attributeSearch}
                    onChange={handleAttributeSearch}
                />
                {filteredAttributes.length > 0 && (
                    <div className="product-search-results">
                        {filteredAttributes.map((attr) => (
                            <div
                                key={attr.id}
                                className="product-select-card"
                                onClick={() => handleAttributeSelect(attr)}
                            >
                                <div className="product-info">
                                    <strong>{attr.name}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {selectedAttributes.map((attribute) => (
                <div key={attribute.id} className="inv-attr-block">
                    <div className="inv-attr-header">
                        <strong>{attribute.name}</strong>
                        <div className="inv-attr-header-actions">
                            <button
                                type="button"
                                className="quotation-close"
                                onClick={() => toggleAttributeAccordion(attribute.name)}
                            >
                                {attributeAccordions[attribute.name] ? "Hide" : "Show"}
                            </button>
                            <button
                                type="button"
                                className="quotation-close"
                                onClick={() => handleDeleteAttribute(attribute.id)}
                                aria-label="Remove attribute"
                            >
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>
                    </div>
                    {attributeAccordions[attribute.name] && (
                        <div className="inv-attr-body">
                            <p className="inv-attr-values-label">Value(s):</p>
                            <div className="inv-attr-terms">
                                {(attribute.associatedTerms || []).map((term) => {
                                    const selected = attribute.selectedTerms.some(
                                        (t) => t.id === term.id
                                    );
                                    return (
                                        <button
                                            key={term.id}
                                            type="button"
                                            className={`inv-attr-term ${selected ? "selected" : ""}`}
                                            onClick={() => handleTermClick(attribute, term)}
                                        >
                                            {term.name}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="inv-attr-actions">
                                {(attribute.associatedTerms || []).length > 0 &&
                                    (attribute.selectedTerms.length <
                                    attribute.associatedTerms.length ? (
                                        <button
                                            type="button"
                                            className="ls-apply-btn inv-attr-action-btn"
                                            onClick={(e) => handleSelectAllTerms(e, attribute)}
                                        >
                                            Select all
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="ls-apply-btn inv-attr-action-btn"
                                            onClick={(e) => handleDeselectAllTerms(e, attribute)}
                                        >
                                            Select none
                                        </button>
                                    ))}
                                <button
                                    type="button"
                                    className="ls-apply-btn inv-attr-action-btn"
                                    onClick={(e) => handleSaveAttribute(e, attribute)}
                                >
                                    Save attribute
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}

            {selectedAttributes.length > 0 &&
                savedAttributeIds.length === selectedAttributes.length && (
                    <div className="ls-form-group">
                        <button
                            type="button"
                            className="ls-apply-btn"
                            style={{ width: "100%" }}
                            onClick={handleGenerateVariations}
                        >
                            Generate Variations
                        </button>
                    </div>
                )}

            {variations.length > 0 && (
                <div className="ls-form-group">
                    <label>Variations ({variations.length})</label>
                    <div className="inv-variation-list">
                        {variations.map((variation, index) => {
                            const details = variationDetails[variation.id] || {};
                            const discountType = details.discount_type || "price";
                            return (
                                <div key={variation.id} className="inv-variation-item">
                                    <div
                                        className="inv-variation-header"
                                        onClick={() =>
                                            setActiveVariationIndex((prev) =>
                                                prev === index ? null : index
                                            )
                                        }
                                    >
                                        <span>{getVariationLabel(variation, index)}</span>
                                        <button
                                            type="button"
                                            className="quotation-close"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveVariation(variation.id);
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    {activeVariationIndex === index && (
                                        <div className="inv-variation-body">
                                            <div className="ls-form-group">
                                                <label>Title</label>
                                                <input
                                                    type="text"
                                                    placeholder="Title"
                                                    value={details.title || ""}
                                                    onChange={(e) =>
                                                        handleVariationDetailChange(
                                                            variation.id,
                                                            "title",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="ls-form-group">
                                                <label>Regular Price*</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    placeholder="Enter regular price"
                                                    value={details.price ?? variation.price ?? ""}
                                                    onChange={(e) =>
                                                        handleVariationDetailChange(
                                                            variation.id,
                                                            "price",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="ls-form-group">
                                                <label>Discount Type</label>
                                                <div className="ls-radio-group">
                                                    <label
                                                        className={`ls-radio-option ${discountType === "price" ? "selected" : ""}`}
                                                        onClick={() =>
                                                            handleVariationDetailChange(
                                                                variation.id,
                                                                "discount_type",
                                                                "price"
                                                            )
                                                        }
                                                    >
                                                        Price
                                                    </label>
                                                    <label
                                                        className={`ls-radio-option ${discountType === "percentage" ? "selected" : ""}`}
                                                        onClick={() =>
                                                            handleVariationDetailChange(
                                                                variation.id,
                                                                "discount_type",
                                                                "percentage"
                                                            )
                                                        }
                                                    >
                                                        Percentage
                                                    </label>
                                                </div>
                                            </div>
                                            {discountType === "price" ? (
                                                <div className="ls-form-group">
                                                    <label>Discount Price</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        placeholder="Enter discount price"
                                                        value={details.discount_price || ""}
                                                        onChange={(e) =>
                                                            handleVariationDetailChange(
                                                                variation.id,
                                                                "discount_price",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                            ) : (
                                                <div className="ls-form-group">
                                                    <label>Discount Percentage (%)</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                        placeholder="Enter discount percentage"
                                                        value={details.discount_percentage || ""}
                                                        onChange={(e) =>
                                                            handleVariationDetailChange(
                                                                variation.id,
                                                                "discount_percentage",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}
                                            <div className="ls-form-group">
                                                <label>Sale Price</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    placeholder="Sale price"
                                                    value={details.salePrice ?? variation.salePrice ?? ""}
                                                    readOnly
                                                />
                                            </div>
                                            <div className="ls-form-group">
                                                <label>SKU*</label>
                                                <input
                                                    type="text"
                                                    placeholder="SKU"
                                                    value={(
                                                        details.sku ||
                                                        variation.sku ||
                                                        ""
                                                    ).toUpperCase()}
                                                    onChange={(e) =>
                                                        handleVariationDetailChange(
                                                            variation.id,
                                                            "sku",
                                                            e.target.value.toUpperCase()
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="ls-form-group">
                                                <label>Stock</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    placeholder="Enter stock"
                                                    value={details.stock ?? variation.stock ?? ""}
                                                    onChange={(e) =>
                                                        handleVariationDetailChange(
                                                            variation.id,
                                                            "stock",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="ls-form-group">
                <label>Stock Status</label>
                <select
                    value={stockStatus}
                    onChange={(e) => setStockStatus(e.target.value)}
                    className="ls-categoryservice"
                >
                    {STOCK_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                            {opt}
                        </option>
                    ))}
                </select>
            </div>
            {renderCategoryField()}
            <div className="ls-form-group">
                <label>HSN Code</label>
                <input
                    type="text"
                    placeholder="Enter HSN code"
                    value={hsn}
                    onChange={(e) => setHsn(e.target.value)}
                />
            </div>
            {renderStatusField()}
        </>
    );

    if (!isOpen || !productType) return null;

    return createPortal(
        <>
            <div className="ls-enquiry-modal">
                <div className={`ls-enquiry-modal-content${productType === "Variable Product" ? " ls-enquiry-modal-content--wide" : ""}`}>
                    <div className="quotation-header">
                        <h2>
                            <i className={PRODUCT_ICONS[productType]} /> {productType}
                        </h2>
                        <button type="button" className="quotation-close" onClick={onClose}>
                            <i className="fa-solid fa-xmark" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {/* —— Basic info —— */}
                        <div className="ls-form-group">
                            <label>Product Name*</label>
                            <input
                                type="text"
                                placeholder="Enter product name"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                            />
                        </div>

                        <div className="ls-form-group">
                            <label>Short Description</label>
                            <textarea
                                placeholder="Enter short description..."
                                value={shortDescription}
                                onChange={(e) => setShortDescription(e.target.value)}
                                rows={3}
                            />
                        </div>

                        {renderImageField()}

                        {productType === "Simple Product" && (
                            <>
                                {renderPricingFields("MRP (Original Price)*")}
                                <div className="ls-form-group">
                                    <label>SKU*</label>
                                    <input
                                        type="text"
                                        placeholder="Enter SKU"
                                        value={sku}
                                        onChange={(e) => setSku(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="ls-form-group">
                                    <label>Stock Status*</label>
                                    <select
                                        value={stockStatus}
                                        onChange={(e) => setStockStatus(e.target.value)}
                                        className="ls-categoryservice"
                                    >
                                        {STOCK_OPTIONS.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {stockStatus !== "Unlimited Stock" && (
                                    <div className="ls-form-group">
                                        <label>Total Stock*</label>
                                        <input
                                            type="number"
                                            min="0"
                                            placeholder="Enter available quantity"
                                            value={totalStock}
                                            onChange={(e) => setTotalStock(e.target.value)}
                                        />
                                    </div>
                                )}
                                {renderCategoryField()}
                                <div className="ls-form-group">
                                    <label>HSN Code</label>
                                    <input
                                        type="text"
                                        placeholder="Enter HSN code"
                                        value={hsn}
                                        onChange={(e) => setHsn(e.target.value)}
                                    />
                                </div>
                                {renderStatusField()}
                            </>
                        )}

                        {productType === "Variable Product" && renderVariableProductFields()}

                        {productType === "Grouped Product" && (
                            <>
                                <div className="ls-form-group">
                                    <label>Search &amp; Add Products*</label>
                                    <input
                                        type="text"
                                        placeholder="Search products..."
                                        value={productSearch}
                                        onChange={(e) => setProductSearch(e.target.value)}
                                    />
                                    {filteredProducts.length > 0 && (
                                        <div className="product-search-results">
                                            {filteredProducts.slice(0, 8).map((product) => (
                                                <div
                                                    key={product.product_id || product.id}
                                                    className="product-select-card"
                                                    onClick={() => addGroupedProduct(product)}
                                                >
                                                    {product.image && (
                                                        <img src={`${url}${product.image}`} alt="" />
                                                    )}
                                                    <div className="product-info">
                                                        <strong>{product.title}</strong>
                                                        <p>
                                                            ₹
                                                            {product.discountPrice ||
                                                                product.salePrice ||
                                                                product.originalPrice}
                                                            {product.sku ? (
                                                                <span className="sku"> (SKU: {product.sku})</span>
                                                            ) : null}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {groupedProducts.length > 0 && (
                                    <div className="ls-form-group">
                                        <label>Grouped Products ({groupedProducts.length})</label>
                                        <div className="recorded-note-lists">
                                            {groupedProducts.map((product) => (
                                                <div
                                                    key={product.product_id || product.id}
                                                    className="recorded-note-items"
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        gap: 8,
                                                    }}
                                                >
                                                    <span>{product.title}</span>
                                                    <button
                                                        type="button"
                                                        className="quotation-close"
                                                        onClick={() =>
                                                            removeGroupedProduct(product.product_id)
                                                        }
                                                        aria-label="Remove"
                                                    >
                                                        <i className="fa-solid fa-xmark" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {renderPricingFields("Original Price*")}
                                <div className="ls-form-group">
                                    <label>SKU*</label>
                                    <input
                                        type="text"
                                        placeholder="Enter SKU"
                                        value={sku}
                                        onChange={(e) => setSku(e.target.value)}
                                        required
                                    />
                                </div>
                                {renderCategoryField()}
                                {renderStatusField()}
                            </>
                        )}

                        <div className="ls-modal-action">
                            <button type="button" className="ls-modal-cancel" onClick={onClose}>
                                Cancel
                            </button>
                            {loading ? (
                                <button type="button" className="ls-apply-btn">
                                    Submitting...
                                </button>
                            ) : (
                                <button type="submit" className="ls-apply-btn">
                                    Submit
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>

            <ImageModalEquip
                show={showImageModal}
                handleClose={() => setShowImageModal(false)}
                images={images}
                onImageSelect={(img) => {
                    setSelectedImage(img);
                    setShowImageModal(false);
                }}
                multiple={false}
                setImages={setImages}
                elevated
            />
        </>,
        document.body
    );
};

export default InvoiceAddProductFormModal;
